import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BadgesList } from './ProfileBadges';
import { getInitialsAvatar, getResolvedAvatar } from '../../utils/avatarCache';

/**
 * Achievements dialog (Figma 862:2982): the per-family progress cards in a dialog, so the
 * profile header isn't active behind them. Settings (787:16758) chooses which clubs count.
 */
export default function AchievementsModal({ open, onClose, badges, uid, focusId, clubs = [], excludedClubIds = [], onSaveExcluded }) {
  const [view, setView] = useState('list'); // 'list' | 'settings'

  useEffect(() => {
    if (!open) return undefined;
    setView('list');
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal ach-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="achievements-title"
        onClick={(e) => e.stopPropagation()}
      >
        {view === 'settings' ? (
          <AchievementSettings
            clubs={clubs}
            excludedClubIds={excludedClubIds}
            onBack={() => setView('list')}
            onSave={async (ids) => {
              await onSaveExcluded(ids);
              setView('list');
            }}
          />
        ) : (
          <>
            <div className="profile-menu__header">
              <button type="button" className="event-page__icon-btn" onClick={() => setView('settings')} aria-label="Achievement settings">
                <span className="material-symbols-rounded" aria-hidden="true">settings</span>
              </button>
              <h3 id="achievements-title" className="achievement__title">Achievements</h3>
              <button type="button" className="event-page__icon-btn" onClick={onClose} aria-label="Close">
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </div>
            <div className="ach-modal__body">
              <p className="ach-modal__intro">Your combined stats and achievements across all your clubs.</p>
              <BadgesList badges={badges} uid={uid} focusId={focusId} />
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function AchievementSettings({ clubs, excludedClubIds, onBack, onSave }) {
  const [excluded, setExcluded] = useState(() => new Set(excludedClubIds));
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setExcluded((prev) => {
    const nextSet = new Set(prev);
    if (nextSet.has(id)) nextSet.delete(id);
    else nextSet.add(id);
    return nextSet;
  });

  return (
    <>
      <div className="profile-menu__header">
        <button type="button" className="event-page__icon-btn" onClick={onBack} aria-label="Back to achievements">
          <span className="material-symbols-rounded" aria-hidden="true">arrow_back</span>
        </button>
        <h3 id="achievements-title" className="achievement__title">Achievement settings</h3>
        <span className="event-page__icon-btn" aria-hidden="true" />
      </div>
      <div className="ach-modal__body">
        <p className="ach-modal__intro">Choose which clubs count toward your overall stats and achievements.</p>
        {clubs.map((club) => {
          const counted = !excluded.has(club.id);
          return (
            <button
              key={club.id}
              type="button"
              className={`ach-modal__club${counted ? ' is-counted' : ''}`}
              onClick={() => toggle(club.id)}
              aria-pressed={counted}
            >
              <img
                src={club.photoUrl ? getResolvedAvatar(club.photoUrl) : getInitialsAvatar(club.name)}
                alt=""
                className="avatar avatar-border ach-modal__club-logo"
              />
              <span className="ach-modal__club-name">{club.name}</span>
              {counted && <span className="material-symbols-rounded ach-modal__check" aria-hidden="true">check</span>}
            </button>
          );
        })}
        <button
          type="button"
          className="btn btn-primary btn-full ach-modal__save"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave([...excluded]);
            } finally {
              setSaving(false);
            }
          }}
        >
          <span className="btn-text">{saving ? 'SAVING…' : 'SAVE'}</span>
        </button>
      </div>
    </>
  );
}
